import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import debounce from 'lodash.debounce';
import * as R from 'ramda';

import UserGroupIcon from '@atom/components/common/UserGroupIcon';
import client from '@atom/graph/client';
import { GET_USER_GROUP_TREE, GET_USER_GROUPS } from '@atom/graph/userGroup';
import { Checkbox, Icon, List, Progress, TextField } from '@atom/mui';
import { Resource } from '@atom/types/policy';
import {
  UserGroup,
  UserGroupsConnection,
  UserGroupsConnectionInput,
  UserGroupsTreeConnectionInput,
  UserGroupTreeType,
} from '@atom/types/userGroups';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import PolicyModalContext from '../PolicyModalContext';

import UserGroupTree from './UserGroupTree';

import classes from './policyUserGroupSelection.module.css';

const { ListItem, ListItemText } = List;

const SEARCH_THRESHOLD = 2;
const DEBOUNCE_TIME = 500;

const styles = {
  close: {
    cursor: 'pointer',
  },
  input: {
    boxSizing: 'border-box',
    padding: '1rem 0',
  },
};

const PolicyUserGroupSelection = () => {
  const { state, updateState } = useContext(PolicyModalContext);
  const { grants } = state;

  const [search, setSearch] = useState<string>('');
  const [userGroupTree, setUserGroupTree] = useState<UserGroupTreeType>(null);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [noResults, setNoResults] = useState<boolean>(false);
  const [loadingUserGroups, setLoadingUserGroups] = useState<boolean>(false);

  const selectedUserGroupIds = useMemo(
    () => new Set(grants.map(({ id }) => id)),
    [grants],
  );

  const [getUserGroupTree, { loading: loadingUserGroupTree }] = useLazyQuery<
    { userGroupsTree: UserGroupTreeType },
    { input: UserGroupsTreeConnectionInput }
  >(GET_USER_GROUP_TREE, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: res => setUserGroupTree(res.userGroupsTree),
  });

  const getUserGroups = useCallback(
    async (input: UserGroupsConnectionInput) => {
      setLoadingUserGroups(true);

      const { data } = await client.query<
        { userGroups: UserGroupsConnection },
        { input: UserGroupsConnectionInput }
      >({
        query: GET_USER_GROUPS,
        fetchPolicy: 'network-only',
        variables: { input },
      });

      setUserGroups(data.userGroups.userGroups);
      setLoadingUserGroups(false);
      setNoResults(isNilOrEmpty(data.userGroups.userGroups));
    },
    [],
  );

  useEffect(() => {
    getUserGroupTree({
      variables: {
        input: {
          showAdmin: hasRolePermissions(ROLE_SETS.ADMIN),
        },
      },
    });
  }, []);

  const debouncedSearch = useCallback(
    debounce((name: string) => {
      getUserGroups({ name, showAdmin: hasRolePermissions(ROLE_SETS.ADMIN) });
    }, DEBOUNCE_TIME),
    [],
  );

  const handleSearch = (value: string) => {
    setSearch(value);
    setUserGroups([]);

    if (!value) {
      debouncedSearch.cancel();
      getUserGroupTree({
        variables: {
          input: {
            showAdmin: hasRolePermissions(ROLE_SETS.ADMIN),
          },
        },
      });
    } else if (value.length >= SEARCH_THRESHOLD) {
      debouncedSearch(value);
    } else {
      debouncedSearch.cancel();
    }
  };

  const handleChange = (tree: UserGroupTreeType) => {
    const isSelected = selectedUserGroupIds.has(tree.id);
    const updated = isSelected
      ? grants.filter(({ id }) => id !== tree.id)
      : [...grants, { id: tree.id, name: tree.groupPath.join(' / ') }];

    return updateState({ grants: updated });
  };

  const getAllDescendantGroupsDictionary = (
    userGroup: UserGroupTreeType,
  ): { [key: string]: Resource } => {
    return userGroup.userGroups.reduce(
      (acc: { [key: string]: Resource }, userGroupItem: UserGroupTreeType) => {
        return {
          ...acc,
          [userGroupItem.id]: {
            id: userGroupItem.id,
            name: userGroupItem.groupPath.join(' / '),
          },
          ...getAllDescendantGroupsDictionary(userGroupItem),
        };
      },
      {},
    );
  };

  const handleToggleDescendants = (
    userGroup: UserGroupTreeType,
    selectAll: boolean,
  ) => {
    const allDescendantGroupsDictionary =
      getAllDescendantGroupsDictionary(userGroup);
    const grantsDictionary = grants.reduce(
      (acc: { [key: string]: Resource }, grantItem: Resource) => {
        return {
          ...acc,
          [grantItem.id]: grantItem,
        };
      },
      {},
    );

    if (selectAll) {
      const updatedSelectedIds = new Set([
        ...R.keys(grantsDictionary),
        ...R.keys(allDescendantGroupsDictionary),
      ]);
      const updatedGrants = [...updatedSelectedIds].map(id => {
        return allDescendantGroupsDictionary[id] || grantsDictionary[id];
      });

      updateState({ grants: updatedGrants });
    } else {
      const updatedGrants = grants.filter(
        grant => !R.includes(grant.id, R.keys(allDescendantGroupsDictionary)),
      );

      updateState({ grants: updatedGrants });
    }
  };

  const isSearch = !!search.length;
  const loading = isSearch ? loadingUserGroups : loadingUserGroupTree;

  const userGroupsDictionary = useMemo(() => {
    return userGroups.reduce((acc, userGroup) => {
      const groupPath = R.dropLast(1, userGroup.groupPath).join(' / ');

      return acc[groupPath]
        ? { ...acc, [groupPath]: [...acc[groupPath], userGroup] }
        : { ...acc, [groupPath]: [userGroup] };
    }, {});
  }, [userGroups]);

  const showSearchResults = isSearch && !noResults;
  const showEmptyText = isSearch && noResults;

  return (
    <>
      <TextField
        name="search"
        value={search}
        onChange={event => handleSearch(event.target.value)}
        placeholder="Search"
        style={styles.input}
        slotProps={{
          input: {
            startAdornment: <Icon>search</Icon>,
            endAdornment: search && (
              <Icon style={styles.close} onClick={() => handleSearch('')}>
                close
              </Icon>
            ),
          },
        }}
      />
      {loading ? (
        <div className={classes.progress}>
          <Progress />
        </div>
      ) : (
        <div>
          {!isSearch && (
            <UserGroupTree
              userGroupTree={userGroupTree}
              selected={selectedUserGroupIds}
              onToggle={userGroup => handleChange(userGroup)}
              handleToggleDescendants={handleToggleDescendants}
            />
          )}
          {showSearchResults &&
            R.keys(userGroupsDictionary).map(
              (section: string, index: number) => (
                <div key={section + index} className={classes.sectionContainer}>
                  <div className={classes.groupPathTitle} key={section}>
                    {section}
                  </div>
                  <div>
                    {userGroupsDictionary[section].map(
                      (userGroup: UserGroup) => (
                        <ListItem key={userGroup.id} disableGutters>
                          <Checkbox
                            checked={selectedUserGroupIds.has(userGroup.id)}
                            onChange={() => handleChange(userGroup)}
                          />
                          <div className={classes.avatar}>
                            <UserGroupIcon colorId={userGroup.colorId} />
                          </div>
                          <ListItemText primary={userGroup.name} />
                        </ListItem>
                      ),
                    )}
                  </div>
                </div>
              ),
            )}
          {showEmptyText && <div>No results</div>}
        </div>
      )}
    </>
  );
};

export default PolicyUserGroupSelection;
